import type { BookletEntry, BookletReading } from "./booklet-entry.js";
import { element } from "./dom.js";
import {
  type FlowPart,
  type FlowRow,
  noteLines,
  SheetFlow,
  sheetHanzi,
} from "./sheet-flow.js";
import type { SheetSentence } from "./sheet-sentences.js";
import { pageRoom, type SheetLabels, sized } from "./worksheet-page.js";
import type { WorksheetPaper } from "./worksheet-state.js";

/**
 * A revision booklet's pages: the words a reader chose, set the way their
 * 词典 entries set them, and the sentences that go with them.
 *
 * The words and the sentences flow on `SheetFlow`, the layout the look-alike
 * and vocabulary sheets share, under a contents part that says where each
 * section and each page of words begins. The writing practice and the
 * self-test are pages of their own, appended after, and every page is
 * numbered once the whole booklet is drawn. `#1068` is the issue and
 * `.claude/notes/revision-booklet-design.md` the design.
 */

/** One word to print, with the sentence it is shown by where it has one. */
export interface BookletWord {
  readonly entry: BookletEntry;
  readonly example: SheetSentence | undefined;
}

/** What a booklet is drawn from. */
export interface BookletContents {
  /** The words, in the order they print. */
  readonly words: readonly BookletWord[];

  /** The source's own 例句, for the section after the words. */
  readonly sentences: readonly SheetSentence[];

  /** The writing practice pages, drawn already, where they are asked for. */
  readonly writing: readonly HTMLElement[];

  /** Whether the self-test pages are asked for. */
  readonly test: boolean;
}

/** About how wide a letter prints at the booklet's body size, in mm. */
const letterWidth = 1.55;

/** A 汉字 at the body size, and at the size a sentence is printed in. */
const hanziWidth = { body: 3.6, sentence: 4.6 } as const;

/** Each kind of line's height, in millimetres. */
const lineHeights = {
  head: 10,
  reading: 5,
  sentence: 6.5,
  small: 4.6,
  contents: 6,
} as const;

/** The room under each block, and what it can give up. */
const blockGap = { height: 3.5, least: 1.5 } as const;

/**
 * How many lines a run of text takes in a column so many millimetres wide,
 * at a body text size. `noteLines` measures at the notes' 3 mm, so the column
 * is narrowed by the ratio of the two sizes.
 */
function linesOf(text: string, width: number, letter = letterWidth): number {
  return noteLines(text, width * (1.3 / letter));
}

/** How many lines a 汉字 sentence takes, which breaks between any two. */
function hanziLines(text: string, width: number, size: number): number {
  return Math.max(1, Math.ceil((Array.from(text).length * size) / width));
}

/** A reading's syllables, each coloured by its tone as the 词典 colours it. */
function readingSpans(reading: BookletReading): HTMLElement {
  const { syllables, tones } = reading.said;

  return element("span", {
    className: "cb-wb-py",
    attributes: { lang: "zh-Latn-CN-pinyin" },
    children: syllables.map((syllable, at) =>
      element("span", {
        // `readingsOf` reads a tone with every syllable, so the two align.
        className: `py-syllable py-tone-${String(tones[at]!)}`,
        text: syllable,
      }),
    ),
  });
}

/** The meanings of one reading as one line of text. */
function meaningText(reading: BookletReading): string {
  return reading.meanings.join("; ");
}

/** The classifiers as one line of text. */
function classifierText(entry: BookletEntry): string {
  return entry.classifiers
    .map((one) => `${one.liangci} ${one.pinyin}: ${one.phrase} ${one.english}`)
    .join(" · ");
}

/** A sentence's three lines: the 汉字, the pinyin and the English. */
function sentenceLines(sentence: SheetSentence): readonly HTMLElement[] {
  return [
    sheetHanzi(sentence.text, "cb-wb-sentence"),
    element("p", {
      className: "cb-wb-sentence-py",
      text: sentence.pinyin,
      attributes: { lang: "zh-Latn-CN-pinyin" },
    }),
    element("p", { className: "cb-wb-sentence-en", text: sentence.english }),
  ];
}

/** How tall a sentence's three lines are in a column so wide. */
function sentenceHeight(sentence: SheetSentence, width: number): number {
  return (
    hanziLines(sentence.text, width, hanziWidth.sentence) *
      lineHeights.sentence +
    linesOf(sentence.pinyin, width) * lineHeights.small +
    linesOf(sentence.english, width) * lineHeights.small
  );
}

/** One word's block, as a row of the flow. */
export function wordRow(
  word: BookletWord,
  width: number,
  origin: string,
): FlowRow {
  const { entry, example } = word;
  const head = element("p", {
    className: "cb-wb-head",
    children: [
      element("a", {
        className: "cb-wb-headword",
        attributes: {
          href: `${origin}/cidian/${encodeURIComponent(entry.word)}/`,
        },
        children: [sheetHanzi(entry.word)],
      }),
      ...(entry.fanti === undefined
        ? []
        : [
            element("span", {
              className: "cb-wf-hz cb-wb-fanti",
              text: entry.fanti,
              attributes: { lang: "zh-Hant" },
            }),
          ]),
      ...(entry.band === undefined
        ? []
        : [element("span", { className: "cb-wb-band", text: entry.band })]),
    ],
  });
  const readings = entry.readings.map((reading) =>
    element("p", {
      className: "cb-wb-reading",
      children: [
        readingSpans(reading),
        element("span", {
          className: "cb-wb-means",
          text: meaningText(reading),
        }),
      ],
    }),
  );
  const classifiers = classifierText(entry);
  let height =
    lineHeights.head +
    entry.readings.reduce(
      (sum, reading) =>
        sum +
        linesOf(`${reading.pinyin} ${meaningText(reading)}`, width) *
          lineHeights.reading,
      0,
    );
  const children: Node[] = [head, ...readings];
  if (classifiers !== "") {
    children.push(
      element("p", {
        className: "cb-wb-classifiers",
        children: [
          element("span", { className: "cb-wb-label", text: "Classifiers " }),
          sheetHanzi(classifiers, "cb-wb-classifier-text"),
        ],
      }),
    );
    height +=
      linesOf(`Classifiers ${classifiers}`, width) * lineHeights.reading;
  }
  if (example !== undefined) {
    children.push(
      element("div", {
        className: "cb-wb-example",
        children: sentenceLines(example),
      }),
    );
    height += sentenceHeight(example, width - 4) + 1.5;
  }
  const node = element("div", {
    className: "cb-wb-word",
    // `bookletEntry` gives every entry a reading, its own or the joined one.
    attributes: { "data-cb-pinyin": entry.readings[0]!.pinyin },
    children,
  });

  return {
    node,
    height: height + blockGap.height,
    least: height + blockGap.least,
  };
}

/** One of the source's sentences, numbered, as a row of the flow. */
function sentenceRow(
  sentence: SheetSentence,
  number: number,
  width: number,
): FlowRow {
  const height = sentenceHeight(sentence, width - 8);

  return {
    node: element("div", {
      className: "cb-wb-listed",
      attributes: { "data-cb-sentence": "" },
      children: [
        element("span", { className: "cb-wf-num", text: String(number) }),
        element("div", { children: sentenceLines(sentence) }),
      ],
    }),
    height: height + blockGap.height,
    least: height + blockGap.least,
  };
}

/** One line of the contents. */
interface ContentsLine {
  readonly text: string;
  readonly page: number;
  readonly indented: boolean;
}

/** The contents as rows: a line per section and per page of words. */
function contentsRows(lines: readonly ContentsLine[]): readonly FlowRow[] {
  return lines.map((line) => ({
    node: sized(
      element("p", {
        className: `cb-wb-contents${line.indented ? " cb-wb-contents-sub" : ""}`,
        children: [
          element("span", { text: line.text }),
          element("span", {
            className: "cb-wb-contents-page",
            text: String(line.page),
          }),
        ],
      }),
      { height: lineHeights.contents },
    ),
    height: lineHeights.contents,
  }));
}

/** Where the flowed sections landed, read off the drawn pages. */
interface Landed {
  /** Each page of words, by the first and last word's reading. */
  readonly wordPages: readonly { page: number; from: string; to: string }[];
  readonly words: number | undefined;
  readonly sentences: number | undefined;
}

/** Which page each section and each word starts on. */
function landed(pages: readonly HTMLElement[]): Landed {
  const wordPages: { page: number; from: string; to: string }[] = [];
  let words: number | undefined;
  let sentences: number | undefined;
  for (const [at, page] of pages.entries()) {
    const found = [
      ...page.querySelectorAll<HTMLElement>("[data-cb-pinyin]"),
      // Each block was selected by that attribute.
    ].map((block) => block.dataset["cbPinyin"]!);
    if (found.length > 0) {
      words ??= at + 1;
      wordPages.push({
        page: at + 1,
        from: found[0]!,
        to: found.at(-1)!,
      });
    }
    if (page.querySelector("[data-cb-sentence]") !== null) {
      sentences ??= at + 1;
    }
  }

  return { wordPages, words, sentences };
}

/** How many self-test cells go across a page. */
const testColumns = 3;

/** A self-test cell's height, in millimetres. */
const testCellHeight = 21;

/** Draws revision booklets. */
export class BookletSheet {
  readonly #paper: WorksheetPaper;
  readonly #labels: SheetLabels;

  constructor(properties: {
    readonly paper: WorksheetPaper;
    readonly labels: SheetLabels;
  }) {
    this.#paper = properties.paper;
    this.#labels = properties.labels;
  }

  /** Every page of the booklet, numbered. */
  pages(contents: BookletContents): readonly HTMLElement[] {
    const width = pageRoom(this.#paper).width;
    const testPages = contents.test ? this.#testPages(contents.words) : [];
    // The contents names pages the flow has yet to place, and adds lines of
    // its own to the flow. A few rounds settle it: a contents line moves the
    // words at most a page, and the next round names that page.
    let lines: readonly ContentsLine[] = [];
    let flowed: readonly HTMLElement[] = [];
    for (let round = 0; round < 4; round += 1) {
      flowed = this.#flow(contents, width, lines);
      const next = this.#contentsLines(
        landed(flowed),
        flowed.length,
        contents,
        testPages.length,
      );
      if (sameLines(next, lines)) {
        break;
      }
      lines = next;
    }

    // The self-test's first page of words has to be the front of a sheet,
    // or its answers land on the next sheet rather than on its back.
    const before = flowed.length + contents.writing.length;
    const blank =
      testPages.length > 0 && before % 2 === 1 ? [this.#page([])] : [];

    return numbered([...flowed, ...contents.writing, ...blank, ...testPages]);
  }

  #flow(
    contents: BookletContents,
    width: number,
    lines: readonly ContentsLine[],
  ): readonly HTMLElement[] {
    const parts: FlowPart[] = [
      {
        title: "Contents",
        chinese: "目录",
        rows: contentsRows(lines),
      },
      {
        number: 1,
        title: "Words",
        chinese: "词语",
        note: "Readings, meanings and classifiers for each word, plus an example sentence where available.",
        rows: contents.words.map((word) =>
          wordRow(word, width, this.#labels.origin),
        ),
      },
    ];
    if (contents.sentences.length > 0) {
      parts.push({
        number: 2,
        title: "Sentences",
        chinese: "例句",
        note: "Example sentences from this List using the booklet’s words.",
        rows: contents.sentences.map((sentence, at) =>
          sentenceRow(sentence, at + 1, width),
        ),
      });
    }

    return new SheetFlow({
      paper: this.#paper,
      kind: "Revision booklet",
      kindChinese: "复习手册",
      parts,
      labels: this.#labels,
    }).pages();
  }

  #contentsLines(
    where: Landed,
    flowedPages: number,
    contents: BookletContents,
    testPages: number,
  ): readonly ContentsLine[] {
    const lines: ContentsLine[] = [];
    if (where.words !== undefined) {
      lines.push({ text: "Words 词语", page: where.words, indented: false });
      for (const page of where.wordPages) {
        lines.push({
          text:
            page.from === page.to ? page.from : `${page.from} to ${page.to}`,
          page: page.page,
          indented: true,
        });
      }
    }
    if (where.sentences !== undefined) {
      lines.push({
        text: "Sentences 例句",
        page: where.sentences,
        indented: false,
      });
    }
    if (contents.writing.length > 0) {
      lines.push({
        text: "Writing practice 书写练习",
        page: flowedPages + 1,
        indented: false,
      });
    }
    if (testPages > 0) {
      lines.push({
        text: "Self-test 自测",
        page: oddPage(flowedPages + contents.writing.length + 1),
        indented: false,
      });
    }

    return lines;
  }

  /**
   * The self-test: a page of words in Chinese alone, then a page of the same
   * words' readings and meanings, by turns.
   *
   * The answers' columns run the other way. Printed on both sides of the
   * sheet, flipped on its long edge, each answer is then on the back of its
   * word.
   */
  #testPages(words: readonly BookletWord[]): readonly HTMLElement[] {
    const room = pageRoom(this.#paper);
    const rows = Math.max(
      1,
      Math.floor((room.height - lineHeights.head) / testCellHeight),
    );
    const perPage = rows * testColumns;
    const pages: HTMLElement[] = [];
    for (let at = 0; at < words.length; at += perPage) {
      const chunk = words.slice(at, at + perPage);
      pages.push(
        this.#testPage("Self-test", "自测", chunk, at, false),
        this.#testPage("Self-test answers", "答案", chunk, at, true),
      );
    }

    return pages;
  }

  #testPage(
    title: string,
    chinese: string,
    words: readonly BookletWord[],
    first: number,
    answers: boolean,
  ): HTMLElement {
    const cells = words.map((word, at) => {
      const number = element("span", {
        className: "cb-wf-num",
        text: String(first + at + 1),
      });
      // `bookletEntry` gives every entry a reading, its own or the joined one.
      const reading = word.entry.readings[0]!;

      return sized(
        element("div", {
          className: `cb-wb-test${answers ? " cb-wb-test-answer" : ""}`,
          children: answers
            ? [
                number,
                readingSpans(reading),
                element("span", {
                  className: "cb-wb-test-means",
                  text: reading.meanings.slice(0, 2).join("; "),
                }),
              ]
            : [number, sheetHanzi(word.entry.word, "cb-wb-test-word")],
        }),
        { height: testCellHeight },
      );
    });
    const rows: HTMLElement[] = [];
    for (let at = 0; at < cells.length; at += testColumns) {
      const row = cells.slice(at, at + testColumns);
      while (row.length < testColumns) {
        row.push(element("div", { className: "cb-wb-test cb-wb-test-empty" }));
      }
      rows.push(
        element("div", {
          className: "cb-wb-test-row",
          children: answers ? row.toReversed() : row,
        }),
      );
    }

    return this.#page([
      sized(
        element("header", {
          className: "cb-wf-running",
          children: [
            element("strong", { text: this.#labels.title }),
            element("span", {
              className: "cb-wf-eyebrow",
              children: [document.createTextNode(title), sheetHanzi(chinese)],
            }),
          ],
        }),
        { height: lineHeights.head },
      ),
      ...rows,
    ]);
  }

  /** A page in the frame `SheetFlow` draws, numbered later. */
  #page(children: readonly Node[]): HTMLElement {
    const size = pageRoom(this.#paper).paper;
    const page = element("section", {
      className: "cb-ws-page cb-wf-page",
      attributes: { "data-paper": this.#paper },
      children: [
        element("div", { className: "cb-ws-body", children }),
        element("footer", {
          className: "cb-ws-footer",
          children: [
            element("a", {
              text: this.#labels.site,
              attributes: { href: this.#labels.address },
            }),
            element("span"),
          ],
        }),
      ],
    });

    return sized(page, { width: size.width, height: size.height, padding: 12 });
  }
}

/** The page a section starting at this one starts on, held to a front. */
function oddPage(page: number): number {
  return page % 2 === 0 ? page + 1 : page;
}

/** Whether two sets of contents lines say the same. */
function sameLines(
  one: readonly ContentsLine[],
  other: readonly ContentsLine[],
): boolean {
  return (
    one.length === other.length &&
    one.every(
      (line, at) =>
        line.text === other[at]?.text && line.page === other[at]?.page,
    )
  );
}

/**
 * Number every page of the booklet once, in its footer and its label.
 *
 * The flow, the writing practice and the self-test each number their own
 * pages from one. A booklet is one document.
 */
function numbered(pages: readonly HTMLElement[]): readonly HTMLElement[] {
  for (const [at, page] of pages.entries()) {
    const said = `Page ${String(at + 1)} of ${String(pages.length)}`;
    page.setAttribute("aria-label", said);
    const footer = page.querySelector(".cb-ws-footer span:last-child");
    if (footer !== null) {
      footer.textContent = said;
    }
  }

  return pages;
}
